import { omit } from 'lodash';
import { useRouter } from 'next/router';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Attribute,
  AttributeOptions,
  Category,
  CategoryPreview,
  RootCategory,
  ViewTypes,
} from 'src/api/v1-api';
import { SortOption } from 'src/constants/sort-options';
import { useGlobalSearch } from './globalSearch';

interface AttributeSelectorContextProps {
  attributes: Attribute[];
  stagedAttributes: AttributeOptions;
  setStagedAttributes: (attributeOptions: AttributeOptions) => void;
  stagedPriceRange: [number, number];
  setStagedPriceRange: (range: [number, number]) => void;
  maxPrice: number;
  commitStagedAttributes: () => void;
  selectedAttributes: AttributeOptions;
  setSelectedAttributes: (attributeOptions: AttributeOptions) => void;
  useToggle: boolean;
  viewType: string;
  setViewType: (type: string) => void;
  priceRange: [number, number];
  setPriceRange: (range: [number, number]) => void;
  sortOptions: SortOption[] | null;
  selectedSort: SortOption | null;
  setSelectedSort: (sort: SortOption | null) => void;
  page: number;
  setPage: (page: number) => void;
  isInitialized: boolean;
  featured: boolean;
  setFeatured: (featured: boolean) => void;
  categoryInFilters: Category | RootCategory | CategoryPreview | undefined;
  setCategoryInFilters: (category: Category | RootCategory | CategoryPreview | undefined) => void;
}

const AttributeSelectorContext = createContext<AttributeSelectorContextProps>({
  attributes: [],
  stagedAttributes: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setStagedAttributes: () => {},
  stagedPriceRange: [0, 0],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setStagedPriceRange: () => {},
  maxPrice: 0,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  commitStagedAttributes: () => {},
  selectedAttributes: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedAttributes: () => {},
  useToggle: false,
  viewType: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setViewType: () => {},
  priceRange: [0, 0],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setPriceRange: () => {},
  sortOptions: [],
  selectedSort: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedSort: () => {},
  page: 1,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setPage: () => {},
  isInitialized: false,
  featured: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFeatured: () => {},
  categoryInFilters: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCategoryInFilters: () => {},
});

export const AttributeSelectorProvider = ({
  children,
  attributes,
  useToggle,
  defaultViewType = 'grid',
  maxPrice,
  sortOptions,
  defaultSortOption = null,
  categoryInFilters = undefined,
}: {
  children: React.ReactNode;
  attributes: Attribute[];
  useToggle: boolean;
  defaultViewType: string;
  maxPrice: number | undefined;
  sortOptions: SortOption[] | null;
  defaultSortOption?: SortOption | null;
  categoryInFilters?: Category | RootCategory | CategoryPreview | undefined;
}): JSX.Element => {
  const router = useRouter();
  const { searchQuery, setSearchQuery } = useGlobalSearch();
  const [stagedAttributes, setStagedAttributes] = useState<AttributeOptions>({});
  const [stagedPriceRange, setStagedPriceRangeState] = useState<[number, number]>([0, 0]);
  const [selectedAttributes, setSelectedAttributesState] = useState<AttributeOptions>({});
  const [viewType, setViewType] = useState<string>(defaultViewType);
  const [priceRange, setPriceRangeState] = useState<[number, number]>([0, 0]);
  const [featured, setFeaturedState] = useState<boolean>(false);
  const [categoryHasChanged, setCategoryHasChanged] = useState<boolean>(false);
  const [selectedSort, setSelectedSortState] = useState<SortOption | null>(defaultSortOption);
  const [page, setPageState] = useState(1);
  const [isInitialized, setIsInitialized] = useState(false);
  const [categoryInFiltersState, setCategoryInFilters] = useState<
    Category | RootCategory | CategoryPreview | undefined
  >(categoryInFilters);

  const BLOCK_LIST_ATTRIBUTES = ['id', 'site'];

  useEffect(() => {
    if (maxPrice !== undefined) {
      setPriceRangeState([0, maxPrice]);
    }
  }, [maxPrice]);

  useEffect(() => {
    setCategoryInFilters(categoryInFilters);
  }, [categoryInFilters]);

  const commitStagedAttributes = useCallback(() => {
    setSelectedAttributesState(stagedAttributes);
    setCategoryHasChanged(categoryInFilters?.id !== categoryInFiltersState?.id);
    if (
      stagedPriceRange[0] !== 0 ||
      (stagedPriceRange[1] !== 0 && stagedPriceRange[1] != maxPrice)
    ) {
      setPriceRangeState(stagedPriceRange);
    }
  }, [stagedAttributes, categoryInFilters, categoryInFiltersState, stagedPriceRange, maxPrice]);

  const handleSetSelectedAttributes = useCallback((newAttributes: AttributeOptions) => {
    // Simply filter out BLOCK_LIST_ATTRIBUTES and set the state
    // This maintains pure "set" semantics that components expect
    setSelectedAttributesState(omit(newAttributes, BLOCK_LIST_ATTRIBUTES));
  }, []);

  const parseFilters = () => {
    const url = new URL(window.location.href);
    const filters: Record<string, string> = {};

    // Add selected attributes as filters
    Object.entries(selectedAttributes)
      .filter(([key]) => !BLOCK_LIST_ATTRIBUTES.includes(key))
      .forEach(([key, value]) => {
        filters[key] = Object.values(value).toString();
      });

    // Add price range if set
    if (priceRange[0] !== 0 || (priceRange[1] !== 0 && priceRange[1] != maxPrice)) {
      filters['price'] = `${priceRange[0]}-${priceRange[1]}`;
    }

    // Add sort option if selected
    if (selectedSort) {
      filters['sort'] = selectedSort;
    }

    const searchParams = new URLSearchParams();
    if (Object.keys(filters).length > 0) {
      searchParams.append('filters', JSON.stringify(filters));
    }

    // Preserve non-filter params like `viewType`
    if (viewType && viewType !== '' && viewType !== 'grid') {
      searchParams.append('viewType', viewType);
    }

    if (page > 1) {
      searchParams.append('page', page.toString());
    }

    if (searchQuery) {
      searchParams.append('search', searchQuery);
    }

    if (featured) {
      searchParams.append('featured', 'true');
    }

    if (categoryHasChanged && categoryInFiltersState) {
      url.pathname = `/category/${categoryInFiltersState.id}`;
    }

    const newUrl = searchParams.toString()
      ? `${url.origin}${url.pathname}?${searchParams.toString()}`
      : `${url.origin}${url.pathname}`;

    if (categoryHasChanged) {
      window.location.assign(newUrl);
    } else {
      window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl);
    }
  };

  const handleSetStagedPriceRange = useCallback((range: [number, number]) => {
    setStagedPriceRangeState(range);
  }, []);

  // Parse URL parameters on the initial load
  useEffect(() => {
    if (router.isReady && !isInitialized) {
      parseUrlParameters();
      setIsInitialized(true);
    }
  }, [router.isReady, router.query]);

  useEffect(() => {
    if (isInitialized) {
      parseFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAttributes,
    priceRange,
    viewType,
    selectedSort,
    page,
    searchQuery,
    isInitialized,
    featured,
    categoryHasChanged,
  ]);

  const parseUrlParameters = () => {
    const params = router.query;
    const attributes: AttributeOptions = {};
    let priceRange: string[] = ['0', maxPrice ? maxPrice.toString() : '0'];
    const priceRangeMax = '5000';
    let sortOption: SortOption | null = defaultSortOption;

    if (params.filters) {
      const filters = JSON.parse(params.filters as string);

      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'price') {
          priceRange = (value as string).split('-');
          if (priceRange.length === 1) priceRange.push(priceRangeMax);
          if (Number(priceRange[1]) < Number(priceRange[0])) priceRange[1] = priceRangeMax;
        } else if (key === 'sort') {
          sortOption = value as SortOption;
        } else {
          attributes[key] = (value as string).split(',');
        }
      });
    }

    setPriceRangeState([Number(priceRange[0]), Number(priceRange[1])]);
    setSelectedAttributesState(omit(attributes, BLOCK_LIST_ATTRIBUTES));
    setSelectedSortState(sortOption);

    // Handle other params (viewType, page)
    if (params.viewType && Object.values(ViewTypes).includes(params.viewType as ViewTypes)) {
      setViewType(params.viewType as string);
    }
    if (params.page) {
      setPageState(Number(params.page));
    }
    if (params.search) {
      setSearchQuery(params.search as string);
    }

    setFeaturedState(params.featured === 'true');
  };

  const contextValue = useMemo(
    () => ({
      attributes,
      stagedAttributes,
      setStagedAttributes,
      stagedPriceRange,
      setStagedPriceRange: handleSetStagedPriceRange,
      maxPrice: maxPrice || 0,
      commitStagedAttributes,
      selectedAttributes,
      setSelectedAttributes: handleSetSelectedAttributes,
      useToggle,
      viewType,
      setViewType,
      priceRange,
      setPriceRange: setPriceRangeState,
      sortOptions,
      selectedSort,
      setSelectedSort: setSelectedSortState,
      page,
      setPage: setPageState,
      isInitialized,
      featured,
      setFeatured: setFeaturedState,
      categoryInFilters: categoryInFiltersState,
      setCategoryInFilters,
    }),
    [
      attributes,
      stagedAttributes,
      stagedPriceRange,
      setStagedAttributes,
      handleSetStagedPriceRange,
      maxPrice,
      commitStagedAttributes,
      selectedAttributes,
      handleSetSelectedAttributes,
      useToggle,
      viewType,
      setViewType,
      priceRange,
      setPriceRangeState,
      sortOptions,
      selectedSort,
      setSelectedSortState,
      page,
      setPageState,
      isInitialized,
      featured,
      setFeaturedState,
      categoryInFiltersState,
      setCategoryInFilters,
    ],
  );

  return (
    <AttributeSelectorContext.Provider value={contextValue}>
      {children}
    </AttributeSelectorContext.Provider>
  );
};

export const useAttributeSelector = (): AttributeSelectorContextProps => {
  const context = useContext(AttributeSelectorContext);
  if (context === undefined) {
    throw new Error('useAttributeSelector must be used within a AttributeSelectorProvider');
  }
  return context;
};
